import { noop, getDummyMp4 } from '.';

export default class VideoElement {
  constructor() {
    this.createVideoElement();
  }

  createVideoElement() {
    this.__node = document.createElement('video');
    this.__node.setAttribute('crossOrigin', 'anonymous');
    this.autoPlayCounter = 0;
  }

  get node() {
    if (!this.__node) this.createVideoElement();
    return this.__node;
  }

  set node(videoEl) {
    this.__node = videoEl;
  }

  get paused() {
    return this.node.paused;
  }

  set muted(muted) {
    this.node.muted = muted;
  }

  get muted() {
    return this.node.muted;
  }

  get ended() {
    return this.node.ended;
  }

  get readyState() {
    return this.node.readyState;
  }

  set currentTime(time) {
    this.node.currentTime = time;
  }

  get currentTime() {
    return this.node.currentTime;
  }

  get duration() {
    return this.node.duration;
  }

  set volume(volume) {
    this.node.volume = volume;
  }

  get volume() {
    return this.node.volume;
  }

  set src(src) {
    this.node.src = src;
  }

  get src() {
    return this.node.src;
  }

  set srcObject(src) {
    this.node.srcObject = src;
  }

  get srcObject() {
    return this.node.srcObject;
  }

  get buffered() {
    return this.node.buffered;
  }

  get currentBuffer() {
    return this.duration - this.currentTime || 0;
  }

  play(fallbackMute = true, cb = noop) {
    const playPromise = this.node.play();
    if (playPromise !== undefined && playPromise instanceof Promise) {
      playPromise
        .then(() => {
          cb(true, this.muted);
          this.autoPlayCounter = 0;
        })
        .catch((error) => {
          if (this.autoPlayCounter === 1) {
            this.autoPlayCounter = 0;
            this.muted = false;
            cb(false);
          } else {
            if (fallbackMute && !this.muted) {
              this.muted = true;
              this.autoPlayCounter += 1;
              this.play(fallbackMute, cb);
            } else {
              cb(false);
              this.autoPlayCounter = 0;
            }
          }
        });
    } else {
      cb(true);
    }
  }

  pause() {
    this.node.pause();
  }

  mute() {
    this.node.muted = true;
  }
  unMute() {
    this.node.muted = false;
  }
  load() {
    this.node.load();
    this.isLoaded = true;
  }

  seek(time, incremental = false) {
    // 0.05 is used to make video  tag fire time update event
    const delta = 0.5;

    if (incremental) {
      let newCurrentTime = this.node.currentTime + time;
      newCurrentTime <= 0 && (newCurrentTime = delta);
      if (newCurrentTime >= this.node.duration) {
        this.node.currentTime = this.node.duration - delta;
      } else {
        this.node.currentTime = newCurrentTime;
      }
    } else {
      time >= this.node.duration && (time = this.node.duration - delta);
      time <= 0 && (time = delta);
      this.node.currentTime = time;
    }
  }
  hide() {
    this.node.style.display = 'none';
  }

  show() {
    this.node.style.display = 'block';
  }

  canPlayType(type) {
    return this.node.canPlayType(type);
  }

  showControl() {
    this.node.controls = true;
  }

  hideControl() {
    this.node.controls = false;
  }

  setPoster(posterImage) {
    this.node.poster = posterImage;
  }

  setAttributes(attributes) {
    Object.keys(attributes).forEach((k) => {
      this.node.setAttribute(k, attributes[k]);
    });
  }

  removeAttributes(attributes) {
    Object.keys(attributes).forEach((k) => {
      this.node.removeAttribute(k);
    });
  }

  canAutoPlay(cb = noop, fallbackMute = true) {
    const videoSource = getDummyMp4();
    this.src = videoSource;
    this.play(fallbackMute, (autoPlay, muted) => {
      this.muted = false;
      cb(autoPlay, muted);
    });
  }

  reset() {
    if (this.node) {
      this.node.pause();
      this.node.removeAttribute('src');
      this.node.load();
      if (this.node.parentElement) {
        this.node.parentElement.removeChild(this.node);
      }
    }
    this.node = null;
  }

  async setAutoPlay() {
    const videoSource = getDummyMp4();
    this.src = videoSource;
    this.node.setAttribute('playsinline', '');
    this.node.setAttribute('webkit-playsinline', '');
    try {
      await this.node.play();
    } catch (err) {
      console.error('set auto play', err);
    }
  }
}
